import { useTheme } from "@codat/orchard-ui";
import { ConfigResponse } from "api/shared";
import { createContext, useContext, useEffect, useReducer } from "react";

export enum ConfigStatusType {
  EMPTY,
  READY,
}

type ConfigStatus = ConfigStatusType.EMPTY | ConfigStatusType.READY;

interface State {
  data: ConfigResponse;
  status: ConfigStatus;
}

export enum ActionTypes {
  SET_CONFIG,
}

type Action = { type: ActionTypes.SET_CONFIG; payload: ConfigResponse };

export const reducer = (state: State, action: Action): State => {
  return {
    data: action.payload,
    status: ConfigStatusType.READY,
  };
};

export const defaultState = {
  data: {} as ConfigResponse,
  status: ConfigStatusType.EMPTY,
};

type ConfigContextType = {
  state: State;
  dispatch: React.Dispatch<Action>;
};

const ConfigContext = createContext({} as ConfigContextType);

interface ConfigProviderProps {
  initialState?: State;
}

export function ConfigProvider({
  children,
  initialState = defaultState,
}: React.PropsWithChildren<ConfigProviderProps>) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const theme = useTheme();

  useEffect(() => {
    if (state.data?.colors?.primary) {
      const overrides = {
        action: state.data?.colors?.primary,
      };
      theme.setOverrides(overrides);
    }
    // including theme here causes tests to hang
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <ConfigContext.Provider value={{ state, dispatch }}>
      {children}
    </ConfigContext.Provider>
  );
}

export const useConfig = () => useContext(ConfigContext);
